import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  pdf,
  Font,
  Link,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import parse, { domToReact } from "html-react-parser";

import PoppinsRegular from "../../Assets/Fonts//Poppins/Poppins-Regular.ttf";
import PoppinsBold from "../../Assets/Fonts//Poppins/Poppins-Bold.ttf";
import PoppinsItalic from "../../Assets/Fonts//Poppins/Poppins-Italic.ttf";
import ExportedData from "Assets";

Font.register({
  family: "Poppins",
  fonts: [
    { src: PoppinsRegular, fontWeight: "normal" },
    { src: PoppinsBold, fontWeight: "bold" },
    { src: PoppinsItalic, fontStyle: "italic" },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Poppins",
    fontSize: 10,
    paddingTop: 35,
    paddingBottom: 75,
    paddingLeft: 35,
    paddingRight: 35,
  },
  reportWrapper: {
    fontFamily: "Poppins",
  },
  h2: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
  investigationBody: {
    marginBottom: 12,
    padding: 4,
  },
  listName: {
    backgroundColor: "#f5f5f5",
    marginBottom: 12,
    padding: 10,
  },
  bearingDetails: {
    backgroundColor: "#0056b3",
    padding: 8,
    marginTop: 12,
    marginBottom: 12,
    color: "white",
  },
  h3: {
    fontSize: 12,
    marginBottom: 6,
    marginTop: 12,
  },
  imgAndObsContainer: {
    marginBottom: 10,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    width: "100%",
  },
  imgAndObs: {
    flexDirection: "column",
    justifyContent: "flex-start",
    marginBottom: 10,
    backgroundColor: "#f5f5f5",
    paddingBottom: 20,
  },
  obsImage: {
    width: "100%",
    // height: "auto",
    // height: 250,
    objectFit: "contain",
    padding: 5,
  },
  // obsImage: {
  //   width: "auto",
  //   maxWidth: "100%",
  //   height: "auto",
  //   // maxHeight: 400,
  //   padding: 5,
  //   objectFit: "contain",
  // },
  //   imgAndObs: {
  //   display: "flex",
  //   alignItems: "center",
  //   flexDirection: "column",
  //   justifyContent: "flex-start",
  //   width: "100%",
  //   marginBottom: 10,
  //   backgroundColor: "#f5f5f5",
  //   paddingBottom: 20,
  // },
  // obsImage: {
  //   width: 165,
  //   objectFit: "contain",
  //   padding: 5,
  // },
  download: {
    width: 20,
    height: 20,
    display: "block",
    marginLeft: "auto",
  },
  otherDownload: {
    width: 20,
    height: 20,
  },
  obs: {
    maxWidth: "90%",
    padding: 10,
    flexWrap: "wrap",
  },
  observationBody: {
    backgroundColor: "#f5f5f5",
    padding: 10,
    marginBottom: 8,
    width: "100%",
    // height: "auto",
    paddingBottom: 20,
  },
  observationText: {
    flexWrap: "wrap",
  },
  analysisContainer: {
    padding: 10,
  },
  table: {
    display: "table",
    width: "100%",
    marginVertical: 10,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#003366",
    color: "#fff",
  },

  tableCell: {
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 6, // ⬅ fixes cropped text
    fontSize: 8,
    textAlign: "center",
  },

  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: "#000",
  },

  checkbox: {
    width: 15,
    height: 15,
    borderWidth: 1,
    borderColor: "#000",
  },

  detailsContainer: {
    fontWeight: 500,
    backgroundColor: "#f5f5f5",
    margin: "5% 2% 2% 2%",
    padding: 12,
    borderRadius: 5,
    marginBottom: 12,
  },
  detailsRow: {
    flexDirection: "row",
    marginBottom: 8,
    fontSize: 8,
    fontWeight: 500,
  },
  detailsLabel: {
    minWidth: "40%",
    fontSize: 10,
    color: "#616161",
    flexWrap: "wrap",
  },
  detailsValue: {
    flex: 1,
    fontSize: 10,
    color: "#616161",
    textAlign: "left",
    flexWrap: "wrap",
  },
  sectionAvoidBreak: {
    breakInside: "avoid",
    breakBefore: "auto",
  },
  footerBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 12,
    width: "100%",
  },
});

// Utility to safely get nested values
const safe = (fn, fallback = "-") => {
  try {
    const v = fn();
    return v === null || v === undefined || v === "" ? fallback : v;
  } catch (e) {
    return fallback;
  }
};

// const fetchImageAsBase64 = async (url) => {
//   try {
//     const response = await fetch(url);
//     const blob = await response.blob();
//     return await new Promise((resolve) => {
//       const reader = new FileReader();
//       reader.onloadend = () => resolve(reader.result);
//       reader.readAsDataURL(blob);
//     });
//   } catch {
//     return null;
//   }
// };

const fetchImageAsBase64 = async (url) => {
  try {
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};
const getImageDimensions = (base64) => {
  return new Promise((resolve) => {
    if (!base64 || typeof window === "undefined") {
      resolve(null);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = base64;
  });
};

// const getContainerWidth = (widthPercentStr) => {
//   if (widthPercentStr === "100%") return 525;
//   if (widthPercentStr === "48%") return 252;
//   return 162;
// };
const getContainerWidth = (widthPercentStr) => {
  if (widthPercentStr === "100%") return 525;
  if (widthPercentStr === "60%") return 315;
  if (widthPercentStr === "48%") return 252;
  return 163;
};

// const getImageCardWidth = (img, allImagesInList,mode) => {
//   if (img && img.isLandscape === true) {  // strictly true, not just truthy
//     return "100%";
//   }
//   const verticalCount = (allImagesInList || []).filter(
//     (i) => i.isLandscape !== true,  // strictly not landscape
//   ).length;
//   console.log(verticalCount,mode);

//   if (verticalCount <= 1) {
//     return "60%";
//   }
//   if (verticalCount === 2) {
//     return "48%";
//   }
//   return "31%";
// };

const getImageCardWidth = (img, allImagesInList) => {
  if (img && img.isLandscape === true) {
    return "100%";
  }

  const index = allImagesInList.indexOf(img);

  // Find consecutive portrait group that contains this image
  // Look backwards to find start of current portrait run
  let groupStart = index;
  while (groupStart > 0 && allImagesInList[groupStart - 1].isLandscape !== true) {
    groupStart--;
  }

  // Look forwards to find end of current portrait run
  let groupEnd = index;
  while (groupEnd < allImagesInList.length - 1 && allImagesInList[groupEnd + 1].isLandscape !== true) {
    groupEnd++;
  }

  const groupSize = groupEnd - groupStart + 1;

  if (groupSize <= 1) return "60%";
  if (groupSize === 2) return "48%";
  return "31%";
};
const getImageStyle = (img, widthPercentStr) => {
  const containerWidth = getContainerWidth(widthPercentStr);
  const maxHeight = img && img.isLandscape ? 250 : 350;

  if (img && img.width && img.height) {
    const calculatedHeight = (img.height / img.width) * containerWidth;
    const height = Math.min(maxHeight, calculatedHeight);
    const width = Math.min(containerWidth, (height * img.width) / img.height);
    return {
      width,
      height,
      objectFit: "contain",
      padding: 5,
    };
  }

  return {
    width: containerWidth,
    height: containerWidth * 1.1,
    objectFit: "contain",
    padding: 5,
  };
};

const getAlphabetLabel = (index) => {
  let label = "";
  let i = index;
  while (i >= 0) {
    label = String.fromCharCode((i % 26) + 97) + label;
    i = Math.floor(i / 26) - 1;
  }
  return label;
};

const getImageWidth = (totalImages) => {
  if (!totalImages || totalImages <= 1) {
    return "100%";
  }
  if (totalImages === 2) {
    return "48%";
  }
  return "31%";
};

const decodeHtml = (html) =>
  html
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

const htmlToReactPdf = (html) => {
  const cleanHtml = decodeHtml(html);

  const replace = (domNode) => {
    // if (domNode.type === "text") {
    //   return <Text>{domNode.data}</Text>;
    // }
    if (domNode.type === "text") {
      const normalized = domNode.data.replace(/\s+/g, " ");
      if (normalized.trim() === "") return null;
      return <Text>{normalized}</Text>;
    }

    if (domNode.type === "tag") {
      const children = domToReact(domNode.children, { replace });

      switch (domNode.name) {
        case "b":
        case "strong":
          return <Text style={{ fontWeight: "bold" }}>{children}</Text>;
        case "i":
        case "em":
          return <Text style={{ fontStyle: "italic" }}>{children}</Text>;
        case "u":
          return (
            <Text style={{ textDecoration: "underline" }}>{children}</Text>
          );
        case "br":
          return <Text>{"\n"}</Text>;
        // case "p":
        // case "div":
        //   return (
        //     <View style={{ marginBottom: 8, flexWrap: "wrap" }}>
        //       <Text style={{ flexWrap: "wrap" }}>{children}</Text>
        //     </View>
        //   );
        // case "ul":
        // case "ol":
        //   return (
        //     <View style={{ marginBottom: 8, marginLeft: 12 }}>
        //       {domToReact(domNode.children, { replace })}
        //     </View>
        //   );
        // case "li": {
        //   const parentTag = domNode.parent?.name;
        //   const isOrdered = parentTag === "ol";
        //   const siblings = domNode.parent?.children?.filter(
        //     (n) => n.name === "li",
        //   );
        //   const index = siblings?.indexOf(domNode) ?? 0;
        //   const bullet = isOrdered ? `${index + 1}.` : "•";

        //   return (
        //     <View style={{ marginBottom: 6 }}>
        //       <Text>
        //         {"\u00A0\u00A0\u00A0\u00A0"}
        //         {"\n"}
        //         <Text style={{ marginLeft: 6, marginRight: 6 }}>{bullet}</Text>
        //         {domToReact(domNode.children, { replace })}
        //         {"\n"}
        //       </Text>
        //     </View>
        //   );
        // }
        case "p":
        case "div":
          return (
            <Text>
              {children}
              {"\n"}
            </Text>
          );
        case "ul":
        case "ol":
          return <>{domToReact(domNode.children, { replace })}</>;
        case "li": {
          const parentTag = domNode.parent?.name;
          const isOrdered = parentTag === "ol";
          const siblings = domNode.parent?.children?.filter(
            (n) => n.type === "tag" && n.name === "li",
          );
          const index = siblings?.indexOf(domNode) ?? 0;
          const bullet = isOrdered ? `${index + 1}.` : "•";

          return (
            <Text>
              {"\n"}
              {bullet + " "}
              {domToReact(domNode.children, { replace })}
            </Text>
          );
        }
        default:
          return <Text>{children}</Text>;
      }
    }

    return null;
  };

  return parse(cleanHtml, { replace });
};

const ReportPDF = ({ resultData = {} }) => {
  const body = resultData || {};

  return (
    <Document>
      <Page size="A4" style={{ padding: 0, position: "relative" }}>
        <Image
          src={ExportedData?.Images?.coverImage}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.reportWrapper}>
          {/* 1. Initial Observations by Bearing Components */}
          <View style={styles.sectionAvoidBreak}>
            <Text style={styles.h2}>
              1. Initial Observations by Bearing Components
            </Text>
            {(safe(() => body.bearingComponent, []) || []).map(
              (component, cIdx) => (
                <View key={`comp-${cIdx}`} style={styles.investigationBody}>
                  <View wrap={false}>
                    {component.bearing_number && (
                      <View style={styles.bearingDetails}>
                        <Text>{`Bearing Number - ${component.bearing_number}      ${component?.bearing_name}`}</Text>
                      </View>
                    )}
                    {component.bearing_component_list_name && (
                      <View style={styles.listName} wrap={true}>
                        <Text style={styles.h3}>
                          {`${getAlphabetLabel(cIdx)}. ${safe(() => component.bearing_component_list_name, "")}`}
                        </Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.sectionAvoidBreak}>
                    <Text style={{ fontWeight: 200, marginBottom: 6 }}>
                      Observations
                    </Text>
                    {(component?.kgu_observations || []).length > 0 ? (
                      (component.kgu_observations || []).map((obs, oIdx) => (
                        <View
                          key={`obs-${oIdx}`}
                          style={styles.observationBody}
                        >
                          <Text style={styles.observationText}>
                            {htmlToReactPdf(
                              safe(() => obs.observation_text, ""),
                            )}
                          </Text>
                        </View>
                      ))
                    ) : (
                      <View style={styles.observationBody}>
                        <Text>No observations recorded</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.imgAndObsContainer}>
                    {(component?.kgu_image_with_observations || []).length >
                      0 ? (
                      (component.kgu_image_with_observations || []).map(
                        (img, iIdx) => {
                          const cardWidth = getImageCardWidth(
                            img,
                            component.kgu_image_with_observations,
                          );
                          return (
                            <View
                              key={`img-${iIdx}`}
                              style={[
                                styles.imgAndObs,
                                {
                                  width: cardWidth,
                                  alignSelf:
                                    component.kgu_image_with_observations
                                      .length === 1
                                      ? "center"
                                      : "auto",
                                },
                              ]}
                              wrap={false}
                            >
                              <View
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                  alignItems: "center",

                                }}
                              >
                                <Image
                                  src={img.imgUrl || null}
                                  style={getImageStyle(img, cardWidth)}
                                  wrap={false}
                                />
                                <View style={styles.obs}>
                                  <Text>
                                    {htmlToReactPdf(
                                      safe(() => img.imgObservation, ""),
                                    )}
                                  </Text>
                                </View>
                                {/* Note: links are not clickable in @react-pdf/renderer but we include the URL as text if present */}
                                {img.originalImgUrl ? (
                                  <Link
                                    style={{ fontSize: 8, marginTop: 4 }}
                                    src={img.originalImgUrl}
                                    wrap={false}
                                  >
                                    <Image
                                      src={ExportedData.Icons.downloadIcon}
                                      style={styles.download}
                                      wrap={false}
                                    />
                                  </Link>
                                ) : null}
                              </View>
                            </View>
                          );
                        },
                      )
                    ) : (
                      <Text>No images available</Text>
                    )}
                  </View>
                </View>
              ),
            )}
          </View>

          {/* 2. Primary Modes of Failure */}
          <View style={styles.sectionAvoidBreak}>
            {/* break={true} */}
            <Text style={styles.h2}>2. Primary Modes of Failure</Text>
            {(safe(() => body.modeOfFailure, []) || []).map((mode, mIdx) => (
              <View key={`mode-${mIdx}`} style={styles.investigationBody}>
                <View wrap={false}>
                  {mode.bearing_number && (
                    <View style={styles.bearingDetails}>
                      <Text>{`Bearing Number - ${mode.bearing_number}      ${mode?.bearing_name || ""}`}</Text>
                    </View>
                  )}
                  <View style={styles.listName} wrap={true}>
                    <Text style={styles.h3}>
                      {mode.primary_mode_of_failure_type_name &&
                        `${getAlphabetLabel(mIdx)}. ${safe(() => mode.primary_mode_of_failure_type_name, "")}`}
                    </Text>
                  </View>
                </View>

                <View style={styles.sectionAvoidBreak}>
                  <Text style={{ fontWeight: 600, marginBottom: 6 }}>
                    Observations
                  </Text>
                  {(mode?.kgu_descriptions || []).length > 0 ? (
                    // (mode.kgu_descriptions || []).map((desc, dIdx) => (
                    //   <View key={`md-${dIdx}`}>
                    //     <View style={styles.observationBody}>
                    //       <Text>
                    //         {htmlToReactPdf(
                    //           safe(() => desc.description_text, ""),
                    //         )}
                    //       </Text>
                    //     </View>
                    //     <Text style={{ fontSize: 4 }} />
                    //   </View>
                    (mode.kgu_descriptions || []).map((desc, dIdx) => (
  <View key={`md-${dIdx}`} wrap={false} minPresenceAhead={40}>
    <View style={styles.observationBody}>
      <Text>
        {htmlToReactPdf(safe(() => desc.description_text, ""))}
      </Text>
    </View>
    <Text style={{ fontSize: 4 }} />
  </View>
                    ))
                  ) : (
                    <View style={styles.observationBody}>
                      <Text>No observations recorded</Text>
                    </View>
                  )}
                </View>

                <View style={styles.imgAndObsContainer}>
                  {(mode?.kgu_image_with_failure_observations || []).map(
                    (img, iIdx) => {
                      const cardWidth = getImageCardWidth(
                        img,
                        mode.kgu_image_with_failure_observations,
                      );
                      return (
                        <View
                          key={`pmf-img-${iIdx}`}
                          style={[
                            styles.imgAndObs,
                            {
                              width: cardWidth,
                              alignSelf:
                                mode.kgu_image_with_failure_observations
                                  .length === 1
                                  ? "center"
                                  : "auto",
                            },
                          ]}
                          wrap={false}
                        >
                          <View
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                              alignItems: "center",

                            }}
                          >
                            <Image
                              src={img.imgUrl || null}
                              style={getImageStyle(img, cardWidth)}
                              wrap={false}
                            />
                            <View style={styles.obs}>
                              <Text>
                                {htmlToReactPdf(
                                  safe(() => img.img_cause_of_failure, ""),
                                )}
                              </Text>
                            </View>
                            {img.originalImgUrl ? (
                              <Link
                                style={{ fontSize: 8, marginTop: 4 }}
                                src={img.originalImgUrl}
                                wrap={false}
                              >
                                <Image
                                  src={ExportedData.Icons.downloadIcon}
                                  style={styles.download}
                                  wrap={false}
                                />
                              </Link>
                            ) : null}
                          </View>
                        </View>
                      );
                    },
                  )}
                </View>
              </View>
            ))}
          </View>

          {/* 3. Secondary Modes of Failure */}
          <View style={styles.sectionAvoidBreak} break={true}>
            {/* break={true} */}
            <Text style={styles.h2}>3. Secondary Modes of Failure</Text>
            {(safe(() => body.secondaryModeOfFailure, []) || []).map(
              (sec, sIdx) => (
                <View key={`sec-${sIdx}`} style={styles.investigationBody}>
                  <View wrap={false}>
                    {sec.bearing_number && (
                      <View style={styles.bearingDetails}>
                        <Text>{`Bearing Number - ${sec.bearing_number}      ${sec?.bearing_name || ""}`}</Text>
                      </View>
                    )}
                    <View style={styles.listName} wrap={true}>
                      <Text style={styles.h3}>
                        {sec.additional_mode_of_failure_type_name &&
                          `${getAlphabetLabel(sIdx)}. ${safe(() => sec.additional_mode_of_failure_type_name, "")}`}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.sectionAvoidBreak}>
                    <Text style={{ fontWeight: 600, marginBottom: 6 }}>
                      Observations
                    </Text>
                    {(sec?.kgu_secondary_descriptions || []).length > 0 ? (
                      (sec.kgu_secondary_descriptions || []).map(
                        (desc, dIdx) => (
                          <View key={`sd-${dIdx}`}>
                            <View style={styles.observationBody}>
                              <Text>
                                {htmlToReactPdf(
                                  safe(
                                    () => desc.secondary_description_text,
                                    "",
                                  ),
                                )}
                              </Text>
                            </View>
                            <Text style={{ fontSize: 4 }} />
                          </View>
                        ),
                      )
                    ) : (
                      <View style={styles.observationBody}>
                        <Text>No observations recorded</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.imgAndObsContainer}>
                    {(() => {
                      const images = sec?.kgu_image_with_secondary_failure_observations || [];
                      return images.map((img, iIdx) => {
                        const src = img.imgUrl || img.img_url || null;
                        const desc = img.img_secondary_cause_of_failure;
                        const cardWidth = getImageCardWidth(img, images, 'sec');

                        return (
                          <View
                            key={`smf-img-${iIdx}`}
                            style={[
                              styles.imgAndObs,
                              {
                                width: cardWidth,
                                flexShrink: 0,
                                alignSelf: images.length === 1 ? "center" : "auto",
                              },
                            ]}
                            wrap={false}
                          >
                            <View
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                // alignItems: "center",
                              }}
                            >
                              <Image
                                src={src}
                                style={getImageStyle(img, cardWidth)}
                                wrap={false}
                              />
                              <View style={styles.obs}>
                                <Text>
                                  {htmlToReactPdf(safe(() => desc, ""))}
                                </Text>
                              </View>
                              {img.originalImgUrl ? (
                                <Link
                                  style={{ fontSize: 8, marginTop: 4 }}
                                  src={img.originalImgUrl}
                                  wrap={false}
                                >
                                  <Image
                                    src={ExportedData.Icons.downloadIcon}
                                    style={styles.download}
                                    wrap={false}
                                  />
                                </Link>
                              ) : null}
                            </View>
                          </View>
                        );
                      });
                    })()}
                  </View>
                </View>
              ),
            )}
          </View>

          {/* 4. Overall Bearing Analysis */}
          <View style={styles.sectionAvoidBreak}>
            {/*  break={true} */}
            <Text style={styles.h2}>4. Overall Bearing Analysis</Text>
            {(safe(() => body.bearingAnalysis, []) || []).map(
              (analysis, aIdx) => (
                <View key={`ba-${aIdx}`} style={styles.investigationBody}>
                  {(analysis.bearing_number || analysis.bearingNumber) && (
                    <View style={styles.bearingDetails}>
                      <Text>{`Bearing Number - ${analysis.bearing_number || analysis.bearingNumber}      ${analysis?.bearing_name || analysis?.bearingName || ""}`}</Text>
                    </View>
                  )}

                  <View style={styles.sectionAvoidBreak}>
                    <Text style={{ fontWeight: 600, marginBottom: 6 }}>
                      Observations
                    </Text>
                    {(() => {
                      const kguDescriptions =
                        analysis?.kgu_bearing_analysis_descriptions;
                      if (kguDescriptions && kguDescriptions.length > 0) {
                        return kguDescriptions.map((desc, dIdx) => (
                          <View key={`bad-${dIdx}`}>
                            <View style={styles.observationBody}>
                              <Text>
                                {htmlToReactPdf(
                                  safe(
                                    () =>
                                      desc.bearing_analysis_description_text,
                                    "",
                                  ),
                                )}
                              </Text>
                            </View>
                            <Text style={{ fontSize: 4 }} />
                          </View>
                        ));
                      } else if (
                        Array.isArray(analysis?.analysis) &&
                        analysis.analysis.length > 0
                      ) {
                        return analysis.analysis.map((obs, dIdx) => (
                          <View key={`bad-${dIdx}`}>
                            <View style={styles.observationBody}>
                              <Text>
                                {htmlToReactPdf(safe(() => obs.text, ""))}
                              </Text>
                            </View>
                            <Text style={{ fontSize: 4 }} />
                          </View>
                        ));
                      } else if (analysis?.analysis) {
                        return (
                          <View style={styles.observationBody}>
                            <Text>
                              {htmlToReactPdf(
                                safe(() => analysis.analysis, ""),
                              )}
                            </Text>
                          </View>
                        );
                      } else {
                        return (
                          <View style={styles.observationBody}>
                            <Text>No observations recorded</Text>
                          </View>
                        );
                      }
                    })()}
                  </View>

                  <View style={styles.imgAndObsContainer}>
                    {(() => {
                      const images =
                        analysis?.images_and_description ||
                        analysis?.kgu_image_with_bearing_analysis_observations ||
                        [];
                      return images.map((img, iIdx) => {
                        const src =
                          img.image !== undefined ? img.image : img.img_url;
                        const desc =
                          img.description !== undefined
                            ? img.description
                            : img.img_bearing_analysis_observation;
                        const cardWidth = getImageCardWidth(img, images);

                        return (
                          <View
                            key={`ba-img-${iIdx}`}
                            style={[
                              styles.imgAndObs,
                              {
                                width: cardWidth,
                                alignSelf:
                                  images.length === 1 ? "center" : "auto",
                              },
                            ]}
                            wrap={false}
                          >
                            <View
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                alignItems: "center",

                              }}
                            >
                              <Image
                                src={src || null}
                                style={getImageStyle(img, cardWidth)}
                                wrap={false}
                              />
                              <View style={styles.obs}>
                                <Text>
                                  {htmlToReactPdf(safe(() => desc, ""))}
                                </Text>
                              </View>
                              {img.originalImgUrl ? (
                                <Link
                                  style={{ fontSize: 8, marginTop: 4 }}
                                  src={img.originalImgUrl}
                                  wrap={false}
                                >
                                  <Image
                                    src={ExportedData.Icons.downloadIcon}
                                    style={styles.download}
                                    wrap={false}
                                  />
                                </Link>
                              ) : null}
                            </View>
                          </View>
                        );
                      });
                    })()}
                  </View>
                </View>
              ),
            )}
          </View>

          {/* 5. Conclusions & Recommendations */}
          <View style={styles.sectionAvoidBreak} break={true}>
            <Text style={[styles.h2, { marginTop: 20 }]}>
              5. Conclusions & Recommendations
            </Text>

            {(() => {
              const recData = body.recommendation;
              if (!recData) return <Text>No recommendations available</Text>;
              const items = Array.isArray(recData)
                ? recData
                : recData.conclusion ||
                  recData.recommendation ||
                  recData.conclusion_images
                  ? [recData]
                  : [];

              if (items.length === 0)
                return <Text>No recommendations available</Text>;

              return items.map((rec, rIdx) => (
                <View key={`rec-${rIdx}`} style={styles.investigationBody}>
                  <Text style={styles.h3}>Conclusion</Text>

                  <View style={styles.sectionAvoidBreak}>
                    <View style={styles.observationBody}>
                      <Text style={styles.observationText}>
                        {htmlToReactPdf(safe(() => rec.conclusion, ""))}
                      </Text>
                    </View>
                  </View>

                  {rec.conclusion_images &&
                    rec.conclusion_images.length > 0 && (
                      <View style={styles.imgAndObsContainer}>
                        {rec.conclusion_images.map((img, iIdx) => {
                          const imageSrc =
                            img.image || img.img_url || img.imgUrl;
                          const cardWidth = getImageCardWidth(
                            img,
                            rec.conclusion_images,
                          );
                          return (
                            <View
                              key={`c-img-${iIdx}`}
                              style={[
                                styles.imgAndObs,
                                {
                                  width: cardWidth,
                                  alignSelf:
                                    rec.recommendation_images.length === 1
                                      ? "center"
                                      : "auto",
                                },
                              ]}
                              wrap={false}
                            >
                              <View
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                  alignItems: "center",

                                }}
                              >
                                {imageSrc && (
                                  <Image
                                    src={imageSrc}
                                    style={getImageStyle(img, cardWidth)}
                                    wrap={false}
                                  />
                                )}
                                <View style={styles.obs}>
                                  <Text>
                                    {htmlToReactPdf(
                                      safe(() => img.description, ""),
                                    )}
                                  </Text>
                                </View>
                                {imageSrc ? (
                                  <Link
                                    style={{ fontSize: 8, marginTop: 4 }}
                                    src={img.originalImgUrl || imageSrc}
                                    wrap={false}
                                  >
                                    <Image
                                      src={ExportedData.Icons.downloadIcon}
                                      style={styles.download}
                                      wrap={false}
                                    />
                                  </Link>
                                ) : null}
                              </View>
                            </View>
                          );
                        })}
                      </View>
                    )}

                  <Text style={[styles.h3, { marginTop: 12 }]}>
                    Recommendation
                  </Text>

                  <View style={styles.sectionAvoidBreak}>
                    <View style={styles.observationBody}>
                      <Text style={styles.observationText}>
                        {htmlToReactPdf(safe(() => rec.recommendation, ""))}
                      </Text>
                    </View>
                  </View>

                  {rec.recommendation_images &&
                    rec.recommendation_images.length > 0 && (
                      <View style={styles.imgAndObsContainer}>
                        {rec.recommendation_images.map((img, iIdx) => {
                          const imageSrc =
                            img.image || img.img_url || img.imgUrl;
                          const cardWidth = getImageCardWidth(
                            img,
                            rec.recommendation_images,
                          );
                          return (
                            <View
                              key={`r-img-${iIdx}`}
                              style={[
                                styles.imgAndObs,
                                {
                                  width: cardWidth,
                                },
                              ]}
                              wrap={false}
                            >
                              <View
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                  alignItems: "center",

                                }}
                              >
                                {imageSrc && (
                                  <Image
                                    src={imageSrc}
                                    style={getImageStyle(img, cardWidth)}
                                    wrap={false}
                                  />
                                )}
                                <View style={styles.obs}>
                                  <Text>
                                    {htmlToReactPdf(
                                      safe(() => img.description, ""),
                                    )}
                                  </Text>
                                </View>
                                {imageSrc ? (
                                  <Link
                                    style={{ fontSize: 8, marginTop: 4 }}
                                    src={img.originalImgUrl || imageSrc}
                                    wrap={false}
                                  >
                                    <Image
                                      src={ExportedData.Icons.downloadIcon}
                                      style={styles.download}
                                      wrap={false}
                                    />
                                  </Link>
                                ) : null}
                              </View>
                            </View>
                          );
                        })}
                      </View>
                    )}
                </View>
              ));
            })()}
          </View>

          {/* 6. Appendix */}
          <View style={styles.sectionAvoidBreak}>
            {/* break={true} */}
            <Text style={styles.h2}>6. Appendix</Text>

            <View style={styles.detailsContainer}>
              <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                Bearing Failure Details
              </Text>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Application</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(() => body.appendix.application_or_position, "-"),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Failure Incident</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(() => body.appendix.failure_details, "-"),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>
                  Consequences of the failure
                </Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(() => body.appendix.consequences_of_failure, "-"),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Additional Details</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(() => body.appendix.additional_details, "-"),
                  )}
                </Text>
              </View>

              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: 8,
                  color: "#616161",
                  gap: 6,
                }}
              >
                <Text style={styles.detailsLabel}>Failed Component</Text>
                <View style={{ padding: 6 }}>
                  <Image
                    src={body.appendix?.file || null}
                    // style={{ width: 200, height: 200, marginBottom: 6 }}
                    // style={{ width: "100%", marginBottom: 6 }}
                    style={{
                      width: "auto",
                      maxWidth: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: 6,
                    }}
                    wrap={false}
                  />
                  {body.appendix?.originalFile ? (
                    <Link
                      style={{ fontSize: 8 }}
                      src={body.appendix.originalFile}
                      wrap={false}
                    >
                      <Image
                        src={ExportedData.Icons.downloadIcon}
                        style={styles.otherDownload}
                        wrap={false}
                      />
                    </Link>
                  ) : null}
                </View>
              </View>
            </View>

            <View style={styles.detailsContainer}>
              <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                Asset Details
              </Text>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Plant Area</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details.plant_area,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Machine Type</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details.machine_type,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Machine Part</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details.machine_part,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Maintenance History</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .maintenance_history,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>OEM Name</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .original_equipment_manufacturer_name,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>OEM Model Number</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .oem_model_number,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>OEM Machine Serial</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .oem_machine_serial,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>End User Tag Number</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .end_user_tag_number,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View style={styles.detailsRow}>
                <Text style={styles.detailsLabel}>Additional Details</Text>
                <Text style={styles.detailsValue}>
                  :{" "}
                  {htmlToReactPdf(
                    safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          .additional_details,
                      "-",
                    ),
                  )}
                </Text>
              </View>

              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: 8,
                  color: "#616161",
                  gap: 6,
                }}
              >
                <Text style={styles.detailsLabel}>Application Drawings</Text>
                <View style={{ padding: 6 }}>
                  <Image
                    src={
                      body.appendix?.remote_failure_asset_details
                        ?.upload_application_drawings || null
                    }
                    // style={{ width: 200, height: 200, marginBottom: 6 }}
                    style={{
                      width: "auto",
                      maxWidth: 400,
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: 6,
                    }}
                    wrap={false}
                  />
                  {body.appendix?.remote_failure_asset_details
                    ?.originalUploadApplicationDrawings ? (
                    <Link
                      style={{ fontSize: 8 }}
                      src={
                        body.appendix.remote_failure_asset_details
                          .originalUploadApplicationDrawings
                      }
                      wrap={false}
                    >
                      <Image
                        src={ExportedData.Icons.downloadIcon}
                        style={styles.otherDownload}
                        wrap={false}
                      />
                    </Link>
                  ) : null}
                </View>
              </View>
            </View>

            {/* Bearing Info List */}
            <View style={styles.detailsContainer}>
              <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                Bearing Information
              </Text>

              <View style={{ marginBottom: 8 }}>
                <View style={styles.detailsRow}>
                  <Text style={[styles.detailsLabel, { paddingLeft: 24 }]}>
                    No. of Bearings
                  </Text>
                  <Text style={[styles.detailsValue, { paddingLeft: 4 }]}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_asset_details
                          ?.remote_failure_bearing_info_details
                          ?.remote_failure_bearing_details.length,
                      0,
                    )}
                  </Text>
                </View>
              </View>

              {(
                body.appendix?.remote_failure_asset_details
                  ?.remote_failure_bearing_info_details
                  ?.remote_failure_bearing_details || []
              )
                .reverse()
                .map((bearing, bIdx) => (
                  <View
                    style={[styles.detailsContainer, { margin: 0, marginTop: 10 }]}
                    // wrap={false}
                    key={`bearing-${bIdx}`}
                  >
                    <Text
                      style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}
                    >{`Bearing ${bIdx + 1}`}</Text>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Bearing Type</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.bearing_type_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Bearing Designation
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.bearing_designation, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Bearing Make</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.bearing_make, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Bearing History</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.bearing_history, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Rotational Speed</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.rotational_speed, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Ring Rotation</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.ring_rotation_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Axial Load</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.axial_load, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Radial Load</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.radial_load, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Operating Temperature
                      </Text>
                      <Text style={styles.detailsValue}>
                        :{" "}
                        {safe(() => bearing.bearing_operating_temperature, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Ambient Temperature
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.ambient_temperature, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Environment Contamination Level
                      </Text>
                      <Text style={styles.detailsValue}>
                        :{" "}
                        {safe(
                          () => bearing.environment_contamination_level_name,
                          "-",
                        )}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Vibration Level</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.vibration_level_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Humidity Moisture Level
                      </Text>
                      <Text style={styles.detailsValue}>
                        :{" "}
                        {safe(() => bearing.humidity_moisture_level_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Operating Hours</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.operating_hours_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Mounting Technique
                      </Text>
                      <Text style={styles.detailsValue}>
                        :{" "}
                        {safe(
                          () => bearing.bearing_mounting_technique_name,
                          "-",
                        )}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Mounting Position</Text>
                      <Text style={styles.detailsValue}>
                        :{" "}
                        {safe(
                          () => bearing.bearing_mounting_position_name,
                          "-",
                        )}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Shaft Orientation</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.shaft_orientation_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Shaft Material</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.shaft_material_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Housing Type</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.housing_type_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Housing Material</Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.housing_material_name, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Shaft Housing Sealing
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.shaft_housing_sealing, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        ISO Shaft Fits Tolerance
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.iso_shaft_fits_tolerance, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        ISO Housing Fits Tolerance
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.iso_housing_fits_tolerance, "-")}
                      </Text>
                    </View>

                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>
                        Additional Details
                      </Text>
                      <Text style={styles.detailsValue}>
                        : {safe(() => bearing.additional_details, "-")}
                      </Text>
                    </View>
                  </View>
                ))}
            </View>

            {/* Drive Details */}
            <View style={{ marginBottom: 12 }}>
              {/* break={true} */}
              <View style={styles.detailsContainer}>
                <Text
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}
                >
                  Drive Details
                </Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Power</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () => body.appendix.remote_failure_drive_details.power,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Direct Drive</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_drive_details
                          .direct_drive_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Gear Drive</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_drive_details
                          .gear_drive_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Belt Drive</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_drive_details
                          .belt_drive_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Additional Details</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_drive_details
                          .additional_details,
                      "-",
                    )}
                  </Text>
                </View>
              </View>
            </View>

            {/* Lubrication Details */}
            <View style={{ marginBottom: 12 }}>
              {/* break={true} */}
              <View style={styles.detailsContainer}>
                <Text
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}
                >
                  Lubrication Details
                </Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Lubrication Type</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .lubrication_type_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Lubrication Method</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .lubrication_method_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Lubrication Sample</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .lubrication_sample_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Lubrication Condition</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .lubrication_condition,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Lubrication Make</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .lubrication_make,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>AW/EP Properties</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .has_aw_or_ep_properties_name,
                      "-",
                    )}
                  </Text>
                </View>
              </View>
            </View>

            {/* Physical Inspection */}
            <View style={{ marginBottom: 12 }}>
              {/* break={true} */}
              <View style={styles.detailsContainer}>
                <Text
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}
                >
                  Physical Inspection of Bearing
                </Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Inspection of Bearing</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .inspection_of_bearing_name,
                      "-",
                    )}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Additional Details</Text>
                  <Text style={styles.detailsValue}>
                    :{" "}
                    {safe(
                      () =>
                        body.appendix.remote_failure_lubrication_details
                          .additional_details,
                      "-",
                    )}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <Image
          src={ExportedData?.Images?.pdfBar}
          style={styles.footerBar}
          fixed
        />
      </Page>
    </Document>
  );
};

export const ReportDownload = forwardRef(({ resultData = {}, scores }, ref) => {
  const pdfName = resultData?.appendix?.report_uuid;
  const [isGenerating, setIsGenerating] = useState(false);
  const internalRef = useRef();
  console.log(resultData, "rd");

  useImperativeHandle(ref, () => ({
    async download() {
      try {
        setIsGenerating(true);
        // small wait to ensure any upstream feels settled
        await new Promise((r) => setTimeout(r, 300));

        // Collect all image URLs from resultData
        const allImages = [];
        // (resultData?.bearingComponent || []).forEach(c => {
        //   (c?.kgu_image_with_observations || []).forEach(i => { if (i.imgUrl) allImages.push(i); });
        // });
        // (resultData?.modeOfFailure || []).forEach(m => {
        //   (m?.kgu_image_with_failure_observations || []).forEach(i => { if (i.imgUrl) allImages.push(i); });
        // });
        // (resultData?.secondaryModeOfFailure || []).forEach(s => {
        //   (s?.kgu_image_with_secondary_failure_observations || []).forEach(i => { if (i.img_url) allImages.push({ imgUrl: i.img_url, _ref: i, _key: 'img_url' }); });
        // });

        (resultData?.bearingComponent || []).forEach((c) => {
          (c?.kgu_image_with_observations || []).forEach((i) => {
            if (i.imgUrl) {
              i.originalImgUrl = i.imgUrl;
              allImages.push(i);
            }
          });
        });
        (resultData?.modeOfFailure || []).forEach((m) => {
          (m?.kgu_image_with_failure_observations || []).forEach((i) => {
            if (i.imgUrl) {
              i.originalImgUrl = i.imgUrl;
              allImages.push(i);
            }
          });
        });
        (resultData?.secondaryModeOfFailure || []).forEach((s) => {
          (s?.kgu_image_with_secondary_failure_observations || []).forEach(
            (i) => {
              if (i.img_url) {
                i.originalImgUrl = i.img_url; // Changed from originalImg_url
                i.imgUrl = i.img_url;
                allImages.push(i);
              }
            },
          );
        });

        (resultData?.bearingAnalysis || []).forEach((ba) => {
          (
            ba?.images_and_description ||
            ba?.kgu_image_with_bearing_analysis_observations ||
            []
          ).forEach((i) => {
            const imgUrl = i.image || i.img_url || i.imgUrl;
            if (imgUrl) {
              i.originalImgUrl = imgUrl;
              i.imgUrl = imgUrl;
              allImages.push(i);
            }
          });
        });

        const recData = resultData?.recommendation;
        const recItems = Array.isArray(recData)
          ? recData
          : recData?.conclusion ||
            recData?.recommendation ||
            recData?.conclusion_images
            ? [recData]
            : [];
        recItems.forEach((rec) => {
          (rec?.conclusion_images || []).forEach((i) => {
            const imageSrc = i.image || i.img_url || i.imgUrl;
            if (imageSrc) {
              i.originalImgUrl = imageSrc;
              i.imgUrl = imageSrc;
              allImages.push(i);
            }
          });
          (rec?.recommendation_images || []).forEach((i) => {
            const imageSrc = i.image || i.img_url || i.imgUrl;
            if (imageSrc) {
              i.originalImgUrl = imageSrc;
              i.imgUrl = imageSrc;
              allImages.push(i);
            }
          });
        });

        // Convert all to base64
        // Convert all to base64
        await Promise.all(
          allImages.map(async (imgObj) => {
            const url = imgObj.imgUrl || imgObj.img_url;

            // Proactively load dimensions from original URL (handles localhost dev environment dimensions fetch)
            if (url) {
              try {
                const dims = await getImageDimensions(url);
                if (dims) {
                  imgObj.width = dims.width;
                  imgObj.height = dims.height;
                  imgObj.isLandscape = dims.width / dims.height > 1.2;
                }
              } catch (e) {
                console.error("Failed to load dimensions from original URL", e);
              }
            }

            const base64 = await fetchImageAsBase64(url);
            if (base64) {
              imgObj.imgUrl = base64;
              // For secondary mode images that use img_url
              if (imgObj.img_url) {
                imgObj.img_url = base64;
              }
              if (imgObj.image) {
                imgObj.image = base64;
              }

              // Fallback load dimensions from base64 if not resolved from url
              if (!imgObj.width) {
                const dims = await getImageDimensions(base64);
                if (dims) {
                  imgObj.width = dims.width;
                  imgObj.height = dims.height;
                  imgObj.isLandscape = dims.width / dims.height > 1.2;
                }
              }
            }
          }),
        );

        // Preserve original URLs for appendix images before conversion
        if (resultData?.appendix?.file) {
          resultData.appendix.originalFile = resultData.appendix.file;
        }
        if (
          resultData?.appendix?.remote_failure_asset_details
            ?.upload_application_drawings
        ) {
          resultData.appendix.remote_failure_asset_details.originalUploadApplicationDrawings =
            resultData.appendix.remote_failure_asset_details.upload_application_drawings;
        }

        // Also convert appendix images
        if (resultData?.appendix?.file) {
          resultData.appendix.file =
            (await fetchImageAsBase64(resultData.appendix.file)) ||
            resultData.appendix.file;
        }
        if (
          resultData?.appendix?.remote_failure_asset_details
            ?.upload_application_drawings
        ) {
          resultData.appendix.remote_failure_asset_details.upload_application_drawings =
            (await fetchImageAsBase64(
              resultData.appendix.remote_failure_asset_details
                .upload_application_drawings,
            )) ||
            resultData.appendix.remote_failure_asset_details
              .upload_application_drawings;
        }

        const blob = await pdf(<ReportPDF resultData={resultData} />).toBlob();
        saveAs(blob, pdfName);
        return blob;
      } catch (err) {
        console.error(err);
      } finally {
        setIsGenerating(false);
      }
    },
  }));

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: -1,
        visibility: "hidden",
        width: 600,
        height: 600,
      }}
    >
      {/* Hidden render area to allow chart/image components if needed later */}
      <div aria-hidden>
        {isGenerating ? <span>Generating PDF...</span> : null}
      </div>
    </div>
  );
});

export default ReportDownload;
